// apps/client/src/hooks/mapFirestoreConfigDoc.ts
//
// Pure mapping from a Firestore commission_config doc to the in-memory
// CommissionConfigWithMeta shape consumed by BrokerCommissionSettingsPage
// and DashboardPage.
//
// Extracted from useBrokerCommissionConfig's inline onSnapshot callback
// (PR 1A-v3.2, 2026-05-14) so the field-enumeration is unit-testable
// WITHOUT pulling firebase + jsdom + canvas into the test runner. Three
// silent-failure modes of the credit_analyst feature were rooted in this
// mapper silently dropping new fields (creditAnalystSplits was missed in
// PR #712, #722, #725). Tests in mapFirestoreConfigDoc.test.ts assert
// every commission_config field the page reads is preserved here.
//
// INVARIANT: when adding a new field to the Firestore commission_config
// doc, you MUST update BOTH the FirestoreCommissionConfig type AND this
// mapper AND a test case. Never cast `currentConfig as { newField: ... }`
// in a page — go through this mapper.

import type { Timestamp } from 'firebase/firestore';
import type { CommissionConfig, AssistantSplit, CreditAnalystSplit } from '../utils/commissionSplit';

export interface FirestoreCommissionConfig {
  effectiveFrom: Timestamp;
  effectiveTo: Timestamp | null;
  brokerIntroductionPct: number;
  companyIntroductionPct: number;
  /** PR C — 3-tier extensions. OPTIONAL on Firestore: legacy configs
   *  written before this feature don't have them, and read-side treats
   *  absent trail* fields as "fall back to upfront pct" so existing
   *  tenants are byte-identical. */
  trailBrokerIntroductionPct?: number | null;
  trailCompanyIntroductionPct?: number | null;
  upfrontSelfSourcedPct?: number;
  trailSelfSourcedPct?: number;
  defaultIntroductionType: 'company';
  visiblePaymentTypes?: string[];
  baseSalaryCents: number | null;
  /** Monthly revenue hurdle (2026-10-06). Absent on every config written
   *  before it and on brokers without one. */
  monthlyRevenueHurdleCents?: number | null;
  hurdleClawbackBucket?: 'own' | 'as_tagged' | null;
  hurdleYearEndRule?: 'carry' | 'forgive' | 'clawback' | null;
  hurdleFiscalYearStartMonth?: number | null;
  /** Per-assistant share of the broker's earned commission.
   *  Added 2026-04-19. Absent on historical configs → treat as {}. */
  assistantSplits?: Record<string, AssistantSplit>;
  /** Per-credit-analyst share (PR 1A-v3, 2026-05-13). Separate upfront
   *  and trail percentages so admins can express asymmetric splits.
   *  Legacy entries (pre-PR-1A-v3) had a single `sharePct` — read-shim
   *  in commissionSplit.ts handles that case. Absent on configs that
   *  have never had an analyst allocation. */
  creditAnalystSplits?: Record<string, CreditAnalystSplit>;
  createdAt: Timestamp;
  createdBy: string;
}

export interface CommissionConfigWithMeta extends CommissionConfig {
  id: string;
  createdAt: Date;
  createdBy: string;
}

export function mapFirestoreConfigDoc(
  id: string,
  data: FirestoreCommissionConfig,
): CommissionConfigWithMeta {
  return {
    id,
    brokerIntroductionPct: data.brokerIntroductionPct,
    companyIntroductionPct: data.companyIntroductionPct,
    // null (not 0) for the trail* fields → preserves the "fall back to
    // upfront pct" path in computeBrokerShare so non-3-tier brokers
    // behave byte-identically.
    trailBrokerIntroductionPct: data.trailBrokerIntroductionPct ?? undefined,
    trailCompanyIntroductionPct: data.trailCompanyIntroductionPct ?? undefined,
    // 0 default is safe — no 'self_sourced'-tagged loans exist for
    // non-3-tier brokers, so 0% × $0 = $0.
    upfrontSelfSourcedPct: data.upfrontSelfSourcedPct ?? 0,
    trailSelfSourcedPct: data.trailSelfSourcedPct ?? 0,
    defaultIntroductionType: data.defaultIntroductionType || 'company',
    effectiveFrom: data.effectiveFrom?.toDate?.() ?? new Date(0),
    effectiveTo: data.effectiveTo?.toDate?.() ?? null,
    baseSalaryCents: data.baseSalaryCents ?? null,
    // null (not 0) so revenueHurdle.ts treats "no hurdle" and "never had
    // one" identically.
    monthlyRevenueHurdleCents: data.monthlyRevenueHurdleCents ?? null,
    hurdleClawbackBucket: data.hurdleClawbackBucket ?? null,
    hurdleYearEndRule: data.hurdleYearEndRule ?? null,
    hurdleFiscalYearStartMonth: data.hurdleFiscalYearStartMonth ?? null,
    visiblePaymentTypes: data.visiblePaymentTypes,
    assistantSplits: data.assistantSplits ?? undefined,
    creditAnalystSplits: data.creditAnalystSplits ?? undefined,
    createdAt: data.createdAt?.toDate?.() ?? new Date(0),
    createdBy: data.createdBy || '',
  };
}
